import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type {
  CredentialAuthType,
  CredentialData,
  UpdateCredentialPayload,
} from '@/apis/customerCredentials';

dayjs.extend(relativeTime);

/** PRD 4.1: credential names are 1-60 characters. */
export const NAME_MAX_LENGTH = 60;

/** Stands in for a saved secret. Secrets are write-only, so the edit form
 * never receives the real value; this is a fixed placeholder, not a mask of it. */
export const MASKED_SECRET = '********';

/** "2 hours ago", or "Never" for a credential no run has used yet. */
export const formatLastUsed = (value: string | null | undefined): string =>
  value ? dayjs(value).fromNow() : 'Never';

/** Label of the secret field for a credential type. */
export const secretLabel = (authType?: CredentialAuthType): string =>
  authType === 'OAUTH_CLIENT_CREDENTIALS' ? 'client secret' : 'API key';

/** The Edit form's field values, before they're turned into the PATCH body by `buildUpdatePayload`. */
export interface EditFormValues {
  name: string;
  api_key_param_name?: string;
  oauth_client_id?: string;
  oauth_token_url?: string;
  oauth_scopes?: string;
  /** Set only after "Replace" was used; empty/undefined keeps the stored value. */
  secret?: string;
}

/**
 * The PATCH body for an edit: only what actually changed, so an untouched
 * secret is never sent (and the server keeps the stored one) and a pure rename
 * doesn't look auth-relevant to the backend. Empty when nothing changed.
 */
export const buildUpdatePayload = (
  credential: CredentialData,
  values: EditFormValues
): UpdateCredentialPayload => {
  const payload: UpdateCredentialPayload = {};
  const name = values.name?.trim();
  if (name !== undefined && name !== credential.name) payload.name = name;

  if (credential.auth_type === 'API_KEY') {
    const param = values.api_key_param_name?.trim();
    if (
      param !== undefined &&
      param !== (credential.api_key_param_name ?? '')
    ) {
      payload.api_key_param_name = param;
    }
  } else {
    const clientId = values.oauth_client_id?.trim();
    if (
      clientId !== undefined &&
      clientId !== (credential.oauth_client_id ?? '')
    ) {
      payload.oauth_client_id = clientId;
    }
    const tokenUrl = values.oauth_token_url?.trim();
    if (
      tokenUrl !== undefined &&
      tokenUrl !== (credential.oauth_token_url ?? '')
    ) {
      payload.oauth_token_url = tokenUrl;
    }
    const scopes = values.oauth_scopes?.trim();
    if (scopes !== undefined && scopes !== (credential.oauth_scopes ?? '')) {
      payload.oauth_scopes = scopes;
    }
  }

  if (values.secret) payload.secret = values.secret;
  return payload;
};
